import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { Logger } from '../../../logger/Logger';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { CoreDispatch, useCoreDispatch } from '../../../store/CoreStore';
import { useAutoplaySlice } from '../../../store/slices/autoplay/selectors';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { useBetSlice } from '../../../store/slices/bets/selectors';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { popupActions } from '../../../store/slices/popup/slice';
import { toastActions } from '../../../store/slices/toast/slice';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { validateAndSaveBets } from '../../game/gameControls/autoplayControl/utils/validateAndSaveBets';
import { PopupType } from '../types';

import { BlockingModalContent } from './BlockingModalContent';

export const AutoplayModifiedConnected: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const currentBet = useBetSlice.bets();
  const acceptedBets = useBetSlice.acceptedBets();
  const invalidBets = useBetSlice.invalidBetspots();
  const totalBet = useBetSlice.totalBet();
  const autoplayInProgress = useAutoplaySlice.isInProgress();
  const isGameVisible = useConfigSlice.isGameVisible();

  useEffect(() => {
    if (!autoplayInProgress) {
      /** In case autoplay is stopped for some reason, then autoplay popup needs to be dismissed */
      dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
      /** If autoplay is stopped because of the visibility change, then it means the default option was selected and toaster needs to be displayed accordingly */
      if (!isGameVisible) {
        dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
      }
    }
  }, [autoplayInProgress, dispatch, isGameVisible]);

  const action = [
    <Button
      key="THIS_ROUND_ONLY"
      variant={ButtonVariant.MAIN}
      tk="THIS_ROUND_ONLY"
      onClick={() => {
        dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
        dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
        Logger.logEvent('autoplay_modify', {
          additional: 'Popup_ThisRound',
        });
      }}
    />,
    <Button
      key="MODIFY"
      variant={ButtonVariant.ACCENT}
      tk="MODIFY"
      onClick={() => {
        if (currentBet) {
          validateAndSaveBets({ dispatch, currentBet, acceptedBets, invalidBets });
        }
        dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
        dispatch(toastActions.createToast('AUTOPLAY_BET_AMOUNT_MODIFIED'));
        dispatch(autoplayActions.setActiveBetAmount(totalBet));
        Logger.logEvent('autoplay_modify', {
          additional: 'Popup_AllRounds',
        });
      }}
    />,
  ];

  return (
    <BlockingModalContent title={<L10nText tk="MODIFY_AUTOPLAY" />} actions={action}>
      <L10nText tk="NEW_BET_AMOUNT" /> <CurrencyConnected value={totalBet} />
      <div style={{ paddingTop: '8px' }}>
        <L10nText tk="MODIFY_AUTOPLAY_WITH_NEW_BET" />
      </div>
    </BlockingModalContent>
  );
};

export const autoplayModifiedConnectedOnClose = (dispatch: CoreDispatch): void => {
  dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
};
